import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Star, ImageIcon } from "lucide-react";
import { publicApi, adminApi } from "../../lib/api";
import { resolveAsset } from "../../lib/assetUrl";
import { toast } from "sonner";

const emptyTestimonial = { name: "", location: "", rating: 5, product: "", review: "", image: "" };

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTestimonial);
  const [uploading, setUploading] = useState(false);

  const load = () => publicApi.getTestimonials().then((r) => setTestimonials(r.data)).catch(console.error);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyTestimonial); setDialogOpen(true); };
  const openEdit = (t) => {
    setEditing(t.id);
    setForm({ name: t.name, location: t.location || "", rating: t.rating || 5, product: t.product || "", review: t.review || "", image: t.image || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await adminApi.updateTestimonial(editing, form);
        toast.success("Testimonial updated");
      } else {
        await adminApi.createTestimonial(form);
        toast.success("Testimonial created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await adminApi.deleteTestimonial(id);
      toast.success("Testimonial deleted");
      load();
    } catch (err) {
      toast.error("Error deleting");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      // Store relative URL only - resolved at render time to survive env changes
      setForm((f) => ({ ...f, image: res.data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-slate-900">Testimonials ({testimonials.length})</h2>
        <Button onClick={openCreate} className="bg-sky-500 hover:bg-sky-600 text-white rounded-lg">
          <Plus className="w-4 h-4 mr-2" /> Add Testimonial
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {testimonials.map((t) => (
          <Card key={t.id} className="border-slate-100 overflow-hidden">
            {t.image && (
              <div className="h-40 overflow-hidden">
                <img src={resolveAsset(t.image)} alt={t.product} className="w-full h-full object-cover" />
              </div>
            )}
            <CardContent className="p-4">
              <div className="flex gap-0.5 mb-2">
                {Array.from({ length: t.rating || 5 }).map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm text-slate-600 line-clamp-2 mb-2">"{t.review}"</p>
              <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
              <p className="text-xs text-slate-400">{t.location} — {t.product}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(t)} className="flex-1">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(t.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Testimonial" : "Add Testimonial"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Customer Name</Label>
                <Input value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="Name" />
              </div>
              <div className="space-y-1.5">
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => updateField("location", e.target.value)} placeholder="City" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Product</Label>
                <Input value={form.product} onChange={(e) => updateField("product", e.target.value)} placeholder="e.g. Keyrings" />
              </div>
              <div className="space-y-1.5">
                <Label>Rating (1-5)</Label>
                <Input type="number" min={1} max={5} value={form.rating} onChange={(e) => updateField("rating", parseInt(e.target.value) || 5)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Review</Label>
              <Textarea value={form.review} onChange={(e) => updateField("review", e.target.value)} placeholder="Customer review" rows={3} />
            </div>
            <div className="space-y-1.5">
              <Label>Product Image</Label>
              <div className="flex gap-2">
                <Input value={form.image} onChange={(e) => updateField("image", e.target.value)} placeholder="Image URL" className="flex-1" />
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  <Button type="button" variant="outline" asChild disabled={uploading}>
                    <span><ImageIcon className="w-4 h-4" /></span>
                  </Button>
                </label>
              </div>
              {form.image && <img src={resolveAsset(form.image)} alt="Preview" className="w-full h-32 object-cover rounded-lg mt-2" />}
            </div>
            <Button onClick={handleSave} className="w-full bg-sky-500 hover:bg-sky-600 text-white">
              {editing ? "Update Testimonial" : "Create Testimonial"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
